//LIBRARIES
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Formik, Form, Field } from 'formik';
import FlatButton from '@material-ui/core/Button';
import Lightbox from 'react-image-lightbox';

//HELPERS
import { getCurrentLocale } from 'helpers/i18n';
import { localizeChequeImage } from 'helpers/Localization';
import { colors } from 'helpers/ColorHelper';
import { formatMoney } from 'helpers/Formatter';
import { AccentColor } from 'helpers/AccentColorHelper';
import * as validator from 'helpers/Validator';
import { getTranslatedString } from 'helpers/i18n';
import { padDetailsTypes } from 'helpers/constants';
import { media } from 'helpers/MediaHelper';

//COMPONENTS
import { Input } from 'components/ui/Input';
import Select from 'components/ui/SelectField';
import { Fragment } from 'react';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';

const StyledH3 = styled.h3`
  text-align: center;
`;

const StyledContainer = styled.div`
  display: ${props => (props.isHidden ? 'none' : 'block')};
  ${media.mobile`
    height: 100%;
  `};
`;

const StyledForm = styled(Form)`
  ${media.mobile`
    height: 100%;
    display:flex;
    flex-direction: column;
  `};
`;

const StyledFormCtaContainer = styled.div`
  padding: 1rem 0;
  display: flex;
  justify-content: space-between;
`;

const StyledInput = styled(Input)`
  margin-bottom: 0.5rem;
`;

const StyledSelect = styled(Select)`
  margin-bottom: 0.5rem;
  .ant-select-selection {
    height: 54px;
    padding: 0.5rem;
  }
`;

const StyledSection = styled.div`
  margin-bottom: 0.5rem;
`;

const StyledCardSection = styled.div`
  margin-bottom: 0.5rem;
  ${media.mobile`
    flex: 1;
  `};
`;

const StyledH4 = styled.h4`
  text-align: center;
  background: #f1f1f1;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
`;
const StyledAmountOutstandingP = styled.p`
  margin: 1rem 0;
`;

const StyledErrorP = styled.p`
  color: ${colors.YUHU_RED};
`;

const StyledPaymentAmountInfo = styled.p`
  text-align: center;
  padding: 10px 20px 0px;
`;

const PAYMENT_VALIDATION = {
  TRANSIT_NUMBER_LENGTH: 5,
  INSTITUTION_NUMBER_LENGTH: 3,
  ACCOUNT_NUMBER_MIN_LENGTH: 4,
  ACCOUNT_NUMBER_MAX_LENGTH: 12,
  ACCOUNT_NUMBER_ACH_MIN_LENGTH: 4,
  ACCOUNT_NUMBER_ACH_MAX_LENGTH: 17,
  ROUTING_NUMBER_ACH_LENGTH: 9,
};

export const PaysafeDirectDebitPaymentForm = ({
  outstandingBalance,
  handleSubmit,
  handleClose,
  isHidden,
  lockedAmount,
  padDetailType,
  showFeeInfo,
}) => {
  const [exampleIsOpen, setExampleIsOpen] = useState(false);
  const isEFT = padDetailType === padDetailsTypes.EFT;
  const isACH = padDetailType === padDetailsTypes.ACH;

  const chequeImage = localizeChequeImage(getCurrentLocale(), padDetailType);

  const validatePaymentForm = useCallback(
    async ({
      paymentAmount,
      accountHolderName,
      transitNumber,
      institutionNumber,
      accountNumber,
      routingNumber,
      accountType,
    }) => {
      let errors = {};
      const amountInFloat = parseFloat(String(paymentAmount).replace('$', ''));
      if (isNaN(amountInFloat)) {
        errors.paymentAmount = getTranslatedString('tenantPortal.paysafe.paymentAmountNotNum');
      } else if (amountInFloat < 1) {
        errors.paymentAmount = getTranslatedString(
          'tenantPortal.paysafe.paymentAmountMustBeGreater',
          {
            amount: '$1',
          }
        );
      }
      const required = getTranslatedString('required');

      /** Account Holder Name Validation */
      if (!accountHolderName) errors.accountHolderName = required;

      /** Transit Number Validation */
      if (isEFT) {
        if (!transitNumber) errors.transitNumber = required;
        if (transitNumber.length !== PAYMENT_VALIDATION.TRANSIT_NUMBER_LENGTH) {
          errors.transitNumber = getTranslatedString('transitNumber');
        }
        if (isNaN(transitNumber) || transitNumber.includes('.')) {
          errors.transitNumber = getTranslatedString('transitNumberNaN');
        }
      }

      /** Institution Number Validation */
      if (isEFT) {
        if (!institutionNumber) errors.institutionNumber = required;
        if (institutionNumber.length !== PAYMENT_VALIDATION.INSTITUTION_NUMBER_LENGTH) {
          errors.institutionNumber = getTranslatedString('institutionNumber');
        }
        if (isNaN(institutionNumber) || institutionNumber.includes('.')) {
          errors.institutionNumber = getTranslatedString('institutionNumberNaN');
        }
      }

      /** Account Number Validation */
      if (!accountNumber) errors.accountNumber = required;
      if (
        isEFT &&
        (accountNumber.length < PAYMENT_VALIDATION.ACCOUNT_NUMBER_MIN_LENGTH ||
          accountNumber.length > PAYMENT_VALIDATION.ACCOUNT_NUMBER_MAX_LENGTH)
      ) {
        errors.accountNumber = getTranslatedString('accountNumber');
      }
      if (
        isACH &&
        (accountNumber.length < PAYMENT_VALIDATION.ACCOUNT_NUMBER_ACH_MIN_LENGTH ||
          accountNumber.length > PAYMENT_VALIDATION.ACCOUNT_NUMBER_ACH_MAX_LENGTH)
      ) {
        errors.accountNumber = getTranslatedString('accountNumberACH');
      }
      if (isNaN(accountNumber) || accountNumber.includes('.')) {
        errors.accountNumber = getTranslatedString('accountNumberNaN');
      }

      /** Routing Number Validation */
      if (isACH) {
        if (!routingNumber) errors.routingNumber = required;
        if (routingNumber.length !== PAYMENT_VALIDATION.ROUTING_NUMBER_ACH_LENGTH) {
          errors.routingNumber = getTranslatedString('routingNumber');
        }
        if (isNaN(routingNumber) || routingNumber.includes('.')) {
          errors.routingNumber = getTranslatedString('routingNumberNaN');
        }
      }

      /** Account Type Validation */
      if (isACH && !accountType) {
        errors.accountType = required;
      }

      if (Object.keys(errors).length) {
        throw errors;
      }
    },
    [isEFT, isACH]
  );

  return (
    <StyledContainer isHidden={isHidden}>
      <Formik
        initialValues={{
          paymentAmount: outstandingBalance > 0 ? outstandingBalance : 0,
          transitNumber: '',
          institutionNumber: '',
          accountNumber: '',
          accountHolderName: '',
          routingNumber: '',
          accountType: undefined,
        }}
        validate={validatePaymentForm}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {formikProps => {
          return (
            <StyledForm>
              <StyledSection>
                <StyledH3>
                  {getTranslatedString('tenantPortal.paysafe.confirmPaymentDetails')}
                </StyledH3>
                <StyledH4>
                  {lockedAmount
                    ? getTranslatedString('tenantPortal.paysafe.paymentAmount')
                    : getTranslatedString('tenantPortal.paysafe.howMuchWouldYouLikeToPay')}
                </StyledH4>
                <RenderIfToggleOn toggle="applications.application_fee" dontRenderIf={!showFeeInfo}>
                  <StyledPaymentAmountInfo>
                    {getTranslatedString('tenantPortal.paysafe.applicationFeeInfo')}
                  </StyledPaymentAmountInfo>
                </RenderIfToggleOn>
                <StyledAmountOutstandingP>
                  {getTranslatedString('tenantPortal.paysafe.totalAmount')}:{' '}
                  <b>{formatMoney(outstandingBalance)}</b>
                </StyledAmountOutstandingP>
                {!lockedAmount && (
                  <Field
                    name="paymentAmount"
                    render={({ field }) => (
                      <StyledInput
                        {...field}
                        type="text"
                        value={validator.normalizeMoney(field.value)}
                      />
                    )}
                  />
                )}
                {formikProps.touched.paymentAmount &&
                  formikProps.errors.paymentAmount && (
                    <StyledErrorP>{formikProps.errors.paymentAmount}</StyledErrorP>
                  )}
              </StyledSection>
              <StyledCardSection>
                <StyledH4>{getTranslatedString('paymentDetails')}</StyledH4>
                <Field
                  name="accountHolderName"
                  render={({ field }) => (
                    <StyledInput
                      className="fs-exclude"
                      type="text"
                      placeholder={getTranslatedString('autopay.accountHolderName')}
                      {...field}
                    />
                  )}
                />
                {formikProps.touched.accountHolderName &&
                  formikProps.errors.accountHolderName && (
                    <StyledErrorP>{formikProps.errors.accountHolderName}</StyledErrorP>
                  )}
                {padDetailType === padDetailsTypes.EFT ? (
                  <EFTDetails formikProps={formikProps} />
                ) : (
                  <ACHDetails formikProps={formikProps} />
                )}
                <div className="row" style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setExampleIsOpen(true)}
                  >
                    {getTranslatedString('whereFindDetails')}
                  </button>
                  {exampleIsOpen && (
                    <Lightbox
                      onCloseRequest={() => setExampleIsOpen(false)}
                      mainSrc={chequeImage}
                    />
                  )}
                </div>
              </StyledCardSection>

              <StyledFormCtaContainer>
                <FlatButton
                  onClick={() => {
                    handleClose();
                  }}
                  style={{ color: AccentColor() }}
                >
                  {getTranslatedString('close')}
                </FlatButton>
                <FlatButton
                  type="submit"
                  variant="contained"
                  disabled={false}
                  style={{
                    color: 'white',
                    backgroundColor: false ? colors.LIGHT_GRAY : AccentColor(),
                  }}
                >
                  {getTranslatedString('continue')}
                </FlatButton>
              </StyledFormCtaContainer>
            </StyledForm>
          );
        }}
      </Formik>
    </StyledContainer>
  );
};

const EFTDetails = ({ formikProps }) => (
  <Fragment>
    <Field
      name="transitNumber"
      render={({ field }) => (
        <StyledInput
          className="fs-exclude"
          type="text"
          placeholder={getTranslatedString('autopay.transitBranchNumber')}
          {...field}
        />
      )}
    />
    {formikProps.touched.transitNumber &&
      formikProps.errors.transitNumber && (
        <StyledErrorP>{formikProps.errors.transitNumber}</StyledErrorP>
      )}
    <Field
      name="institutionNumber"
      render={({ field }) => (
        <StyledInput
          className="fs-exclude"
          type="text"
          placeholder={getTranslatedString('autopay.institutionNumber')}
          {...field}
        />
      )}
    />
    {formikProps.touched.institutionNumber &&
      formikProps.errors.institutionNumber && (
        <StyledErrorP>{formikProps.errors.institutionNumber}</StyledErrorP>
      )}
    <Field
      name="accountNumber"
      render={({ field }) => (
        <StyledInput
          className="fs-exclude"
          type="text"
          placeholder={getTranslatedString('autopay.accountNumber')}
          {...field}
        />
      )}
    />
    {formikProps.touched.accountNumber &&
      formikProps.errors.accountNumber && (
        <StyledErrorP>{formikProps.errors.accountNumber}</StyledErrorP>
      )}
  </Fragment>
);

const ACHDetails = ({ formikProps }) => {
  return (
    <Fragment>
      <Field
        name="routingNumber"
        render={({ field }) => (
          <StyledInput
            className="fs-exclude"
            type="text"
            placeholder={getTranslatedString('autopay.routingNumber')}
            {...field}
          />
        )}
      />
      {formikProps.touched.routingNumber &&
        formikProps.errors.routingNumber && (
          <StyledErrorP>{formikProps.errors.routingNumber}</StyledErrorP>
        )}
      <Field
        name="accountNumber"
        render={({ field }) => (
          <StyledInput
            className="fs-exclude"
            type="text"
            placeholder={getTranslatedString('autopay.accountNumber')}
            {...field}
          />
        )}
      />
      {formikProps.touched.accountNumber &&
        formikProps.errors.accountNumber && (
          <StyledErrorP>{formikProps.errors.accountNumber}</StyledErrorP>
        )}
      <Field
        name="accountType"
        render={({ field }) => (
          <StyledSelect
            size="large"
            placeholder={getTranslatedString('autopay.accountType')}
            onChange={value => formikProps.setFieldValue('accountType', value)}
            onBlur={field.onBlur}
            name={field.name}
            value={field.value}
          >
            <Select.Option key="checking" value="checking">
              {getTranslatedString('autopay.checking')}
            </Select.Option>
            <Select.Option key="savings" value="savings">
              {getTranslatedString('autopay.savings')}
            </Select.Option>
            <Select.Option key="loan" value="loan">
              {getTranslatedString('autopay.loan')}
            </Select.Option>
          </StyledSelect>
        )}
      />
      {formikProps.touched.accountType &&
        formikProps.errors.accountType && (
          <StyledErrorP>{formikProps.errors.accountType}</StyledErrorP>
        )}
    </Fragment>
  );
};
